import React from 'react';
import { useParams } from 'react-router-dom';

/**
 * remountOnRouteParams(Component, keyOf) → a component that renders
 * `<Component key={keyOf(params, props)} {...props} />`.
 *
 * THE fix for a page whose state belongs to ONE record but whose route element
 * is reused for another (R14 review, 2026-09-15).
 *
 * 🚨 react-router renders a route's element at the SAME tree position when only
 * a `:param` changes — and when two sibling routes render the same component
 * type (`orders/:id` and `orders/:id/edit` both render `<OrderPage>`). React
 * then keeps the instance, so every `useState` survives into the next record.
 * Measured on OrderPage before this: `/orders/1/edit` → `/orders/2` (bell,
 * Back/Forward) with a file picked kept the instance — "Leave and discard" sent
 * NO discard (nothing unmounted), order 2 opened in EDIT mode on a view URL
 * with the leave guard still armed, and Save CLAIMED order 1's pending file
 * onto order 2. The same shape held a stale `forkId` in the RA block editor
 * and an old RA's content under a new `:id` on RA detail until its refetch.
 *
 * 🔑 A `key` makes "a different record" a different instance: state resets,
 * unmount cleanups run (pending uploads are discarded), and the leave guard
 * unmounts with the page it guarded. Keeping it OUTSIDE `App.js` leaves the
 * route JSX byte-identical (`App.routeParity.test.js`).
 *
 * ⚠️ The cost is a remount — a spinner and a refetch — whenever the key moves,
 * including a page's own post-save redirect onto the new record's URL. Only
 * wrap pages whose state is PER RECORD; a list page that filters by a param
 * would lose its scroll and expansion for nothing.
 */
export default function remountOnRouteParams(Component, keyOf) {
  function RemountOnRouteParams(props) {
    const params = useParams();
    return <Component key={keyOf(params, props)} {...props} />;
  }
  RemountOnRouteParams.displayName = `RemountOnRouteParams(${Component.displayName || Component.name || 'Page'})`;
  return RemountOnRouteParams;
}
